import { Loader } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useQpmCatalogGetReleaseQuery,
  useQpmCatalogItemGetByBaseQuery,
  useQpmCatalogItemsListQuery,
  useQpmCatalogLatestReleaseQuery,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { useMemo } from "react";
import { CatalogMonitorComponent } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/[monitorId]/_components/catalog-monitor-component";

export function CatalogMonitorRoutePage() {
  const navigate = useNavigate();
  const { catalogId, monitorId } = useParams();

  const {
    data: item,
    error,
    isLoading,
  } = useQpmCatalogItemGetByBaseQuery(
    {
      catalogId: catalogId!,
      baseMonitorId: monitorId!,
    },
    {
      skip: !catalogId || !monitorId,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: latestRelease } = useQpmCatalogLatestReleaseQuery(
    catalogId ? { catalogId } : ({} as never),
    { skip: !catalogId },
  );

  const { data: release } = useQpmCatalogGetReleaseQuery(
    latestRelease?.id ? { releaseId: latestRelease.id } : ({} as never),
    { skip: !latestRelease?.id },
  );

  const releaseContainsCurrentMonitor = useMemo(() => {
    if (!release || !monitorId) return false;
    return release.monitors.some((snap) => snap.sourceId === monitorId);
  }, [release, monitorId]);

  const releaseId = releaseContainsCurrentMonitor ? release?.id : undefined;

  const type = item?.snapshot?.type;

  const { data: listRes } = useQpmCatalogItemsListQuery(
    catalogId
      ? {
          catalogId,
          includedOnly: false,
          limit: 0,
          offset: 0,
        }
      : ({} as never),
    {
      skip: !catalogId || !type,
    },
  );

  const sortedIdsOfSameType = useMemo(() => {
    if (!type) return [];
    const items = listRes?.items ?? [];
    return items
      .filter((it) => it.snapshot?.type === type)
      .sort((a, b) =>
        (a.snapshot?.name ?? "").localeCompare(
          b.snapshot?.name ?? "",
          undefined,
          {
            sensitivity: "base",
          },
        ),
      )
      .map((it) => it.baseMonitorId);
  }, [listRes?.items, type]);

  const currentIndex = useMemo(() => {
    if (!monitorId) return -1;
    if (!sortedIdsOfSameType.length) return -1;
    return sortedIdsOfSameType.indexOf(monitorId);
  }, [sortedIdsOfSameType, monitorId]);

  const prevNext = useMemo(() => {
    if (!monitorId) return undefined;
    const total = sortedIdsOfSameType.length;
    const goPrev = () => {
      if (currentIndex <= 0) return;
      const nextId = sortedIdsOfSameType[currentIndex - 1];
      navigate(`../${nextId}`, { replace: true });
    };
    const goNext = () => {
      if (currentIndex < 0 || currentIndex >= total - 1) return;
      const nextId = sortedIdsOfSameType[currentIndex + 1];
      navigate(`../${nextId}`, { replace: true });
    };

    return { currentIndex, total: total || 0, goPrev, goNext };
  }, [monitorId, sortedIdsOfSameType, currentIndex, navigate]);

  if (isLoading) {
    return (
      <div className="flex w-full justify-center p-6">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (!item || !catalogId || !monitorId) return null;

  // Ensure type is a known enum value (used for title rendering).
  const safeType = (type ?? MonitorType.Checkpoint) as MonitorType;

  return (
    <CatalogMonitorComponent
      baseMonitorId={monitorId}
      catalogItem={item}
      monitorType={safeType}
      prevNext={prevNext}
      releaseId={releaseId}
      catalogId={catalogId}
    />
  );
}
